/**
 * IndexedDB ストア
 *
 * Stores:
 * - shoots:  撮影シーン全データ（画像 Blob 含む）
 * - presets: 機材プリセット（Phase 9 では presets.js と並行運用、
 *            Phase 12 で sync.js から参照）
 *
 * クエリは順序維持のため updatedAt の降順 index を使う。
 */

const DB_NAME = 'zone-notes';
const DB_VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('shoots')) {
        const store = db.createObjectStore('shoots', { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('updatedAt', 'updatedAt');
        store.createIndex('developmentPlan', 'developmentPlan');
        store.createIndex('filmId', 'filmId');
      }
      if (!db.objectStoreNames.contains('presets')) {
        const ps = db.createObjectStore('presets', { keyPath: 'id' });
        ps.createIndex('isFavorite', 'isFavorite');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(storeName, mode) {
  return openDb().then((db) => {
    const t = db.transaction(storeName, mode);
    return { tx: t, store: t.objectStore(storeName) };
  });
}

// --------------------------------------------------
// SHOOTS
// --------------------------------------------------

export async function saveShoot(shoot) {
  if (!shoot?.id) throw new Error('shoot.id required');
  const { store } = await tx('shoots', 'readwrite');
  return await new Promise((resolve, reject) => {
    const req = store.put({ ...shoot, updatedAt: Date.now() });
    req.onsuccess = () => resolve(shoot.id);
    req.onerror = () => reject(req.error);
  });
}

export async function getShoot(id) {
  const { store } = await tx('shoots', 'readonly');
  return await new Promise((resolve, reject) => {
    const req = store.get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteShoot(id) {
  const { store } = await tx('shoots', 'readwrite');
  return await new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

/**
 * 全シーンを更新日時降順で取得
 */
export async function listShoots(options = {}) {
  const { limit = 200, planFilter = null, filmIdFilter = null } = options;
  const { store } = await tx('shoots', 'readonly');
  return await new Promise((resolve, reject) => {
    const result = [];
    const idx = store.index('updatedAt');
    const req = idx.openCursor(null, 'prev');
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor && result.length < limit) {
        const v = cursor.value;
        let pass = true;
        if (planFilter && v.developmentPlan !== planFilter) pass = false;
        if (filmIdFilter && v.filmId !== filmIdFilter) pass = false;
        if (pass) result.push(v);
        cursor.continue();
      } else {
        resolve(result);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * テキスト検索（シーン名・場所・notes）
 */
export async function searchShoots(query, limit = 100) {
  const q = (query || '').trim().toLowerCase();
  const all = await listShoots({ limit: 1000 });
  if (!q) return all.slice(0, limit);
  return all.filter((s) =>
    (s.title || '').toLowerCase().includes(q) ||
    (s.location || '').toLowerCase().includes(q) ||
    (s.weather || '').toLowerCase().includes(q) ||
    (s.notes || '').toLowerCase().includes(q) ||
    (s.printing?.lessonLearned || '').toLowerCase().includes(q) ||
    (s.development?.notes || '').toLowerCase().includes(q)
  ).slice(0, limit);
}

/**
 * 振り返り未入力 (development または printing が null) のシーン件数
 */
export async function countPendingReflection() {
  const all = await listShoots({ limit: 200 });
  return all.filter((s) => !s.development || !s.printing).length;
}

/**
 * 最近のシーン (HOME 表示用)
 */
export async function getRecentShoots(n = 5) {
  return await listShoots({ limit: n });
}

// --------------------------------------------------
// 統計
// --------------------------------------------------
export async function aggregateStats() {
  const all = await listShoots({ limit: 1000 });
  if (all.length === 0) return null;

  const planCount = {};
  const filmCount = {};
  const shadowZones = {};
  const highlightZones = {};
  let satisfactionSum = 0, satisfactionN = 0;

  let underN = 0, goodN = 0, overN = 0, devEvalN = 0;
  let flatN = 0, asPlannedN = 0, contrastyN = 0, conN = 0;

  for (const s of all) {
    const plan = s.developmentPlan || 'N';
    planCount[plan] = (planCount[plan] || 0) + 1;
    const fid = s.filmId || 'unknown';
    filmCount[fid] = (filmCount[fid] || 0) + 1;
    if (s.shadowAnchorId) {
      const sz = s.spots?.find((x) => x.id === s.shadowAnchorId)?.placedZone;
      if (sz != null) shadowZones[sz] = (shadowZones[sz] || 0) + 1;
    }
    if (s.highlightAnchorId) {
      const hz = s.spots?.find((x) => x.id === s.highlightAnchorId)?.placedZone;
      if (hz != null) highlightZones[hz] = (highlightZones[hz] || 0) + 1;
    }
    if (s.printing?.satisfaction) {
      satisfactionSum += s.printing.satisfaction;
      satisfactionN++;
    }
    if (s.development?.exposureAccuracy) {
      devEvalN++;
      if (s.development.exposureAccuracy === 'under') underN++;
      else if (s.development.exposureAccuracy === 'good') goodN++;
      else if (s.development.exposureAccuracy === 'over') overN++;
    }
    if (s.development?.contrastResult) {
      conN++;
      if (s.development.contrastResult === 'too-flat') flatN++;
      else if (s.development.contrastResult === 'as-planned') asPlannedN++;
      else if (s.development.contrastResult === 'too-contrasty') contrastyN++;
    }
  }

  return {
    total: all.length,
    planCount,
    filmCount,
    shadowZones,
    highlightZones,
    avgSatisfaction: satisfactionN > 0 ? satisfactionSum / satisfactionN : null,
    exposureBias: devEvalN > 0 ? { underPct: underN / devEvalN * 100, goodPct: goodN / devEvalN * 100, overPct: overN / devEvalN * 100, n: devEvalN } : null,
    contrastBias: conN > 0 ? { flatPct: flatN / conN * 100, asPlannedPct: asPlannedN / conN * 100, contrastyPct: contrastyN / conN * 100, n: conN } : null,
  };
}

// --------------------------------------------------
// PRESETS (Phase 12 で sync.js が利用、Phase 9 では未使用)
// --------------------------------------------------
export async function savePresetToDb(preset) {
  const { store } = await tx('presets', 'readwrite');
  return await new Promise((resolve, reject) => {
    const req = store.put(preset);
    req.onsuccess = () => resolve(preset.id);
    req.onerror = () => reject(req.error);
  });
}
